import type {
  EventResponse,
  EventSummaryResponse,
  RegistrationTypeResponse,
} from '@@/types/api';

/**
 * Whether the event has multiple registration types.
 */
export function isMultiTypeEvent(
  event: EventResponse | EventSummaryResponse,
): boolean {
  return (event.registration_types?.length ?? 0) > 1;
}

/**
 * Returns the default (first) registration type, or synthesizes one from
 * the event-level pricing for backward compatibility.
 */
export function getDefaultRegistrationType(
  event: EventResponse | EventSummaryResponse,
): RegistrationTypeResponse {
  if (event.registration_types?.length) {
    return event.registration_types[0]!;
  }
  // Fallback: synthesize from event-level pricing
  return {
    id: '',
    name: 'Standard',
    description: null,
    display_order: 0,
    accommodation_price_commit: 0,
    accommodation_price_purchase: 0,
    ticket_price_commit: event.pricing.commit.ticket_price,
    ticket_price_purchase: event.pricing.purchase.ticket_price,
    max_registrations: null,
    max_party_size: null,
    pricing: event.pricing,
    registrations_remaining: null,
    is_sold_out: event.is_sold_out ?? false,
  };
}

/**
 * Returns the lowest commit total across all registration types.
 * Falls back to the event-level pricing if no types exist.
 */
export function getLowestCommitPrice(
  event: EventResponse | EventSummaryResponse,
): number {
  if (!event.registration_types?.length) {
    return event.pricing.commit.total;
  }
  return Math.min(
    ...event.registration_types.map((rt) => rt.pricing.commit.total),
  );
}

/**
 * Returns the lowest purchase total across all registration types.
 * Falls back to the event-level pricing if no types exist.
 *
 * Note: the lowest-purchase registration type may differ from the
 * lowest-commit type — they are computed independently.
 */
export function getLowestPurchasePrice(
  event: EventResponse | EventSummaryResponse,
): number {
  if (!event.registration_types?.length) {
    return event.pricing.purchase.total;
  }
  return Math.min(
    ...event.registration_types.map((rt) => rt.pricing.purchase.total),
  );
}

/**
 * Returns registration types that are not sold out.
 */
export function getAvailableTypes(
  event: EventResponse,
): RegistrationTypeResponse[] {
  return (event.registration_types ?? []).filter((rt) => !rt.is_sold_out);
}

/**
 * Looks up a registration type by ID.
 */
export function getRegistrationTypeById(
  event: EventResponse,
  id: string,
): RegistrationTypeResponse | undefined {
  return event.registration_types?.find((rt) => rt.id === id);
}
